import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { apiErrorMessage } from '@/lib/api-client';
import {
  setCorrespondenceFolderSources,
  type Folder,
  type FolderTree as FolderTreeType,
} from '@/hooks/useFolders';
import {
  isAttachmentsFolder,
  topLevelCorrespondenceSources,
} from '@/utils/document-utils';
import { Button } from '@/components/ui/NewButton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FolderTree } from './FolderTree';

interface CorrespondenceFoldersDialogProps {
  matterSlug: string;
  folders: Folder[];
  isOpen: boolean;
  onClose: () => void;
  onUpdated: () => Promise<unknown> | void;
}

/** Manage the explicit sources which feed the flat Correspondence view. */
export const CorrespondenceFoldersDialog = ({
  matterSlug,
  folders,
  isOpen,
  onClose,
  onUpdated,
}: CorrespondenceFoldersDialogProps) => {
  const [selectedFolderGuids, setSelectedFolderGuids] = useState<Set<string>>(new Set());
  const [isApplying, setIsApplying] = useState(false);
  const wasOpen = useRef(false);

  // FolderTree accepts flat input too; supply its display-only tree fields so we can
  // reuse the same hierarchy and folder affordances as Case Files.
  const folderTree = useMemo<FolderTreeType[]>(
    () => folders.map((folder) => ({
      ...folder,
      children: [],
      document_count: 0,
      token_count: 0,
    })),
    [folders],
  );
  const sourceFolderGuids = useMemo(
    () => new Set(topLevelCorrespondenceSources(folders).map((folder) => folder.guid)),
    [folders],
  );
  const folderById = useMemo(
    () => new Map(folders.map((folder) => [folder.folder_id, folder])),
    [folders],
  );

  // Copy saved sources once per open, after folders have loaded. Opening on an
  // empty list would snapshot nothing, and later folder arrivals would be ignored.
  useEffect(() => {
    if (!isOpen) {
      wasOpen.current = false;
      return;
    }
    if (wasOpen.current || folders.length === 0) return;
    setSelectedFolderGuids(new Set(sourceFolderGuids));
    wasOpen.current = true;
  }, [isOpen, folders.length, sourceFolderGuids]);

  const hasSelectedAncestor = (folder: FolderTreeType, selected = selectedFolderGuids) => {
    const visitedFolderIds = new Set<number>();
    let parentId = folder.parent_folder_id;
    while (parentId !== undefined && !visitedFolderIds.has(parentId)) {
      const parent = folderById.get(parentId);
      if (!parent) return false;
      if (selected.has(parent.guid)) return true;
      visitedFolderIds.add(parentId);
      parentId = parent.parent_folder_id;
    }
    return false;
  };

  const canSelectFolder = (folder: FolderTreeType, selected = selectedFolderGuids) => (
    !isAttachmentsFolder(folder) &&
    !hasSelectedAncestor(folder, selected)
  );

  const handleToggleFolder = (folder: FolderTreeType) => {
    if (!canSelectFolder(folder) || isApplying) return;

    setSelectedFolderGuids((current) => {
      const next = new Set(current);
      if (next.has(folder.guid)) {
        next.delete(folder.guid);
      } else {
        next.add(folder.guid);
        // A direct source makes every selected descendant redundant.
        for (const candidate of folders) {
          if (hasSelectedAncestor(candidate as FolderTreeType, next)) {
            next.delete(candidate.guid);
          }
        }
      }
      return next;
    });
  };

  const hasChanges = useMemo(() => (
    selectedFolderGuids.size !== sourceFolderGuids.size ||
    [...selectedFolderGuids].some((folderGuid) => !sourceFolderGuids.has(folderGuid))
  ), [selectedFolderGuids, sourceFolderGuids]);

  const handleDone = async () => {
    if (!wasOpen.current || folders.length === 0) {
      onClose();
      return;
    }
    if (!hasChanges) {
      onClose();
      return;
    }

    setIsApplying(true);
    try {
      await setCorrespondenceFolderSources(matterSlug, [...selectedFolderGuids]);
      await onUpdated();
      toast.success('Correspondence folders updated');
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to update Correspondence folders');
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isApplying && onClose()}>
      <DialogContent className="flex max-h-[80vh] sm:max-w-2xl flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Manage Correspondence folders</DialogTitle>
          <DialogDescription>
            Select the folders that feed this consolidated view. Changes are applied when you finish.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto pr-1">
          <FolderTree
            folders={folderTree}
            totalDocuments={null}
            matterSlug={matterSlug}
            onRefresh={() => undefined}
            hideAllFiles
            showFolderActions={false}
            showDocumentCounts={false}
            selectionEnabled={false}
            defaultExpandAll
            allowExpandWhenDisabled
            selectedFolderGuidsForChecked={selectedFolderGuids}
            onFolderToggleSelection={handleToggleFolder}
            shouldDisableFolder={(folder) => (
              isApplying || !canSelectFolder(folder)
            )}
            renderFolderAccessory={(folder) => {
              if (hasSelectedAncestor(folder)) {
                return <span className="text-xs text-slate-400">Included through parent</span>;
              }
              return null;
            }}
            rowClassName="py-2"
          />
        </div>

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={onClose} disabled={isApplying} text="Cancel" />
          <Button
            onClick={handleDone}
            disabled={isApplying}
            loading={isApplying}
            text={hasChanges ? 'Apply changes' : 'Done'}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
